import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import pb from '../../lib/pocketbase';
import confetti from 'canvas-confetti';

export default function SignatureContratPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [contract, setContract] = useState(null);
    const [loading, setLoading] = useState(true);
    const [signed, setSigned] = useState(false);

    // Workflow state
    const [currentStep, setCurrentStep] = useState(1); // 1: Read, 2: Approve, 3: Sign
    const [agreedTerms, setAgreedTerms] = useState(false);
    const [agreedPrivacy, setAgreedPrivacy] = useState(false);
    const [signerName, setSignerName] = useState('');
    const [isSigning, setIsSigning] = useState(false);

    const canvasRef = useRef(null);
    const contractBodyRef = useRef(null);
    const [ctx, setCtx] = useState(null);

    useEffect(() => {
        const fetchContract = async () => {
            try {
                const res = await pb.collection('contracts').getOne(id, {
                    expand: 'customer,project,quote'
                });
                setContract(res);
                if (res.status === 'signed') setSigned(true);
            } catch (err) {
                // Removed setError as per instructions
            } finally {
                setLoading(false);
            }
        };
        fetchContract();
    }, [id]);

    useEffect(() => {
        if (currentStep === 3 && !signed) {
            // Short delay to ensure canvas is in DOM
            setTimeout(() => {
                const canvas = canvasRef.current;
                if (canvas) {
                    const context = canvas.getContext('2d');
                    canvas.width = canvas.offsetWidth;
                    canvas.height = 250;
                    context.strokeStyle = '#1e293b';
                    context.lineWidth = 3;
                    context.lineCap = 'round';
                    setCtx(context);
                }
            }, 100);
        }
    }, [currentStep, signed]);

    const startDrawing = (e) => {
        setIsSigning(true);
        const rect = canvasRef.current.getBoundingClientRect();
        const x = (e.clientX || (e.touches && e.touches[0].clientX)) - rect.left;
        const y = (e.clientY || (e.touches && e.touches[0].clientY)) - rect.top;
        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const draw = (e) => {
        if (!isSigning) return;
        e.preventDefault();
        const rect = canvasRef.current.getBoundingClientRect();
        const x = (e.clientX || (e.touches && e.touches[0].clientX)) - rect.left;
        const y = (e.clientY || (e.touches && e.touches[0].clientY)) - rect.top;
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const stopDrawing = () => setIsSigning(false);
    const clearSignature = () => ctx && ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    const handleFinalSign = async () => {
        if (!signerName) { alert("Veuillez entrer votre nom complet."); return; }
        const signatureData = canvasRef.current.toDataURL();

        try {
            await pb.collection('contracts').update(id, {
                status: 'signed',
                signature: signatureData,
                signed_at: new Date().toISOString()
            });

            // Notify Admin
            try {
                const adminRes = await pb.collection('users').getList(1, 1, { filter: 'role = "admin"' });
                const adminId = adminRes.items[0]?.id;
                if (adminId) {
                    await pb.collection('messages').create({
                        from: pb.authStore.model?.id || '',
                        to: adminId,
                        subject: 'Contrat signé',
                        content: `Contrat signé : "${contract.title}". Signataire : ${signerName}.`,
                        customer_id: contract.customer
                    });
                }
            } catch { }

            setSigned(true);
            confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 }, colors: ['#4f46e5', '#10b981', '#ffffff'] });
            new Audio('https://assets.mixkit.co/sfx/preview/mixkit-success-fanfare-trumpets-613.mp3').play().catch(() => { });

        } catch (err) {
            alert("Erreur lors de la signature.");
        }
    };

    const renderPlaceholderContent = () => {
        if (!contract) return '';
        let content = contract.content || '';
        content = content.replace(/{{customer_name}}/g, contract.expand?.customer?.name || 'Client');
        content = content.replace(/{{customer_company}}/g, contract.expand?.customer?.company || '');
        content = content.replace(/{{project_name}}/g, contract.expand?.project?.name || 'Projet');
        content = content.replace(/{{quote_number}}/g, contract.expand?.quote?.quote_number || '');
        content = content.replace(/{{total_ttc}}/g, contract.expand?.quote?.total?.toFixed(2) || '0.00');
        content = content.replace(/{{signed_at}}/g, new Date().toLocaleDateString());
        return content;
    };

    if (loading) return <div className="qonto-loading">Chargement sécurisé...</div>;
    // Removed error state, so this block is commented out or removed based on the user's intent.
    // if (error) return <div className="qonto-error">{error}</div>;

    if (signed) {
        return (
            <div className="qonto-success">
                <div className="success-content">
                    <div className="check-icon">✓</div>
                    <h1>Document signé avec succès</h1>
                    <p>Merci {contract.expand?.customer?.name}. Votre accord a été enregistré et transmis à nos équipes.</p>
                    <div className="success-footer">
                        <button onClick={() => navigate('/mon-compte')} className="btn-finish">Aller à mon tableau de bord</button>
                    </div>
                </div>
                <style>{`
                    .qonto-success { min-height: 100vh; background: #f8fafc; display: grid; place-items: center; font-family: 'Inter', sans-serif; text-align: center; }
                    .success-content { background: white; padding: 60px; border-radius: 12px; box-shadow: 0 4px 20px rgba(0,0,0,0.05); max-width: 500px; }
                    .check-icon { width: 80px; height: 80px; background: #10b981; color: white; border-radius: 50%; display: grid; place-items: center; font-size: 40px; margin: 0 auto 30px; }
                    h1 { font-size: 1.5rem; color: #0f172a; margin: 0 0 15px; }
                    p { color: #64748b; line-height: 1.6; }
                    .btn-finish { margin-top: 40px; background: #0f172a; color: white; border: none; padding: 14px 28px; border-radius: 8px; font-weight: 600; cursor: pointer; width: 100%; }
                `}</style>
            </div>
        );
    }

    return (
        <div className="qonto-container">
            {/* Header / Steps */}
            <header className="qonto-header">
                <div className="header-inner">
                    <div className="brand">INFOCOLAB</div>
                    <div className="steps-indicator">
                        <div className={`step-item ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'done' : ''}`}>
                            <span className="step-num">{currentStep > 1 ? '✓' : '1'}</span>
                            <span className="step-label">Lecture</span>
                        </div>
                        <div className="step-line"></div>
                        <div className={`step-item ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'done' : ''}`}>
                            <span className="step-num">{currentStep > 2 ? '✓' : '2'}</span>
                            <span className="step-label">Approbation</span>
                        </div>
                        <div className="step-line"></div>
                        <div className={`step-item ${currentStep >= 3 ? 'active' : ''}`}>
                            <span className="step-num">3</span>
                            <span className="step-label">Signature</span>
                        </div>
                    </div>
                    <div className="header-actions">
                        <button onClick={() => navigate(-1)} className="btn-exit">Quitter</button>
                    </div>
                </div>
            </header>

            <main className="qonto-main">
                {/* Step 1: Reading */}
                {currentStep === 1 && (
                    <div className="view-slide">
                        <div className="document-container">
                            <div className="document-paper" ref={contractBodyRef}>
                                <div dangerouslySetInnerHTML={{ __html: renderPlaceholderContent() }} />
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 2: Approving */}
                {currentStep === 2 && (
                    <div className="view-slide centered">
                        <div className="approval-card">
                            <h2>Approbation du contrat</h2>
                            <p className="subtitle">Veuillez valider les points suivants pour continuer.</p>

                            <div className="checkbox-group">
                                <label className="q-checkbox">
                                    <input type="checkbox" checked={agreedTerms} onChange={e => setAgreedTerms(e.target.checked)} />
                                    <span className="q-check-box"></span>
                                    <div className="q-check-label">
                                        <strong>Acceptation des conditions</strong>
                                        <span>Je confirme avoir lu l'intégralité du document et j'accepte les termes du contrat.</span>
                                    </div>
                                </label>

                                <label className="q-checkbox">
                                    <input type="checkbox" checked={agreedPrivacy} onChange={e => setAgreedPrivacy(e.target.checked)} />
                                    <span className="q-check-box"></span>
                                    <div className="q-check-label">
                                        <strong>Confidentialité</strong>
                                        <span>J'autorise l'usage de ma signature électronique pour ce document spécifique.</span>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 3: Signing */}
                {currentStep === 3 && (
                    <div className="view-slide centered">
                        <div className="signature-focus-card">
                            <h2>Signer le document</h2>
                            <p className="subtitle font-sm">Veuillez entrer votre nom et signer dans l'encadré.</p>

                            <div className="q-input-group">
                                <label>Nom complet du signataire</label>
                                <input
                                    type="text"
                                    placeholder="Ex: Jean Dupont"
                                    value={signerName}
                                    onChange={e => setSignerName(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            <div className="q-pad-container">
                                <div className="pad-tools">
                                    <span>Signature manuscrite</span>
                                    <button onClick={clearSignature}>Effacer</button>
                                </div>
                                <canvas
                                    ref={canvasRef}
                                    onMouseDown={startDrawing}
                                    onMouseMove={draw}
                                    onMouseUp={stopDrawing}
                                    onMouseLeave={stopDrawing}
                                    onTouchStart={startDrawing}
                                    onTouchMove={draw}
                                    onTouchEnd={stopDrawing}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </main>

            {/* Bottom Bar Navigation */}
            <footer className="qonto-footer">
                <div className="footer-inner">
                    <div className="footer-info">
                        <strong>{contract.title}</strong>
                        <span>Référence: {contract.expand?.quote?.quote_number || id.slice(0, 8)}</span>
                    </div>

                    <div className="footer-btns">
                        {currentStep > 1 && (
                            <button className="btn-back" onClick={() => setCurrentStep(prev => prev - 1)}>Retour</button>
                        )}

                        {currentStep === 1 && (
                            <button className="btn-next" onClick={() => setCurrentStep(2)}>Continuer vers l'approbation</button>
                        )}

                        {currentStep === 2 && (
                            <button className="btn-next" disabled={!agreedTerms || !agreedPrivacy} onClick={() => setCurrentStep(3)}>
                                Passer à la signature
                            </button>
                        )}

                        {currentStep === 3 && (
                            <button className="btn-confirm" disabled={!signerName} onClick={handleFinalSign}>
                                Signer et envoyer ✒️
                            </button>
                        )}
                    </div>
                </div>
            </footer>

            <style>{`
                .qonto-container { min-height: 100vh; background: #f1f5f9; font-family: 'Inter', sans-serif; display: flex; flexDirection: column; }
                
                /* Header */
                .qonto-header { height: 72px; background: white; border-bottom: 1px solid #e2e8f0; position: sticky; top: 0; z-index: 100; }
                .header-inner { max-width: 1200px; margin: 0 auto; height: 100%; display: flex; align-items: center; justify-content: space-between; padding: 0 24px; }
                .brand { font-weight: 800; letter-spacing: 1px; color: #0f172a; font-size: 1.1rem; }
                .btn-exit { background: none; border: none; color: #64748b; font-weight: 600; cursor: pointer; font-size: 0.9rem; }
                
                /* Steps Indicator */
                .steps-indicator { display: flex; align-items: center; gap: 12px; }
                .step-item { display: flex; align-items: center; gap: 8px; opacity: 0.4; transition: all 0.3s; }
                .step-item.active { opacity: 1; }
                .step-item.done .step-num { background: #10b981; color: white; border-color: #10b981; }
                .step-num { width: 24px; height: 24px; border: 2px solid #0f172a; border-radius: 50%; display: grid; place-items: center; font-size: 0.75rem; font-weight: 700; color: #0f172a; }
                .step-label { font-size: 0.85rem; font-weight: 600; color: #0f172a; }
                .step-line { width: 40px; height: 1px; background: #e2e8f0; }
                
                /* Main Area */
                .qonto-main { flex: 1; display: flex; padding: 40px 24px 140px; }
                .view-slide { width: 100%; max-width: 800px; margin: 0 auto; animation: fadeInUp 0.4s ease-out; }
                .view-slide.centered { display: flex; align-items: center; justify-content: center; }
                
                /* Document View */
                .document-paper { background: white; padding: 60px; border-radius: 4px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); border: 1px solid #e2e8f0; min-height: 1000px; }
                
                /* Approval Card */
                .approval-card, .signature-focus-card { background: white; padding: 40px; border-radius: 16px; box-shadow: 0 10px 30px rgba(0,0,0,0.05); width: 100%; max-width: 550px; }
                h2 { margin: 0 0 8px; font-size: 1.4rem; color: #0f172a; }
                .subtitle { color: #64748b; margin-bottom: 30px; line-height: 1.5; }
                
                /* Checkboxes */
                .checkbox-group { display: grid; gap: 16px; }
                .q-checkbox { display: flex; gap: 16px; padding: 20px; border: 1px solid #e2e8f0; border-radius: 12px; cursor: pointer; transition: all 0.2s; }
                .q-checkbox:hover { background: #f8fafc; border-color: #cbd5e1; }
                .q-checkbox input { display: none; }
                .q-checkbox input:checked + .q-check-box { background: #0f172a; border-color: #0f172a; }
                .q-checkbox input:checked + .q-check-box::after { content: '✓'; color: white; font-size: 12px; }
                .q-check-box { width: 22px; height: 22px; border: 2px solid #cbd5e1; border-radius: 6px; flex-shrink: 0; margin-top: 2px; display: grid; place-items: center; transition: all 0.2s; }
                .q-check-label { display: flex; flexDirection: column; gap: 4px; }
                .q-check-label strong { font-size: 0.95rem; color: #0f172a; }
                .q-check-label span { font-size: 0.85rem; color: #64748b; line-height: 1.4; }
                
                /* Signature Card */
                .q-input-group { margin-bottom: 24px; }
                .q-input-group label { display: block; font-size: 0.85rem; font-weight: 600; color: #64748b; margin-bottom: 8px; }
                .q-input-group input { width: 100%; padding: 12px 16px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 1rem; outline: none; }
                .q-input-group input:focus { border-color: #0f172a; }
                
                .q-pad-container { border: 1px solid #e2e8f0; border-radius: 12px; background: #f8fafc; overflow: hidden; }
                .pad-tools { display: flex; justify-content: space-between; padding: 10px 16px; font-size: 0.75rem; color: #94a3b8; border-bottom: 1px solid #f1f5f9; }
                .pad-tools button { background: none; border: none; color: #ef4444; font-weight: 600; cursor: pointer; }
                canvas { width: 100%; height: 250px; cursor: crosshair; touch-action: none; }
                
                /* Footer Bar */
                .qonto-footer { position: fixed; bottom: 0; left: 0; right: 0; height: 90px; background: white; border-top: 1px solid #e2e8f0; z-index: 100; box-shadow: 0 -4px 15px rgba(0,0,0,0.03); }
                .footer-inner { max-width: 1200px; margin: 0 auto; height: 100%; display: flex; align-items: center; justify-content: space-between; padding: 0 24px; }
                .footer-info { display: flex; flex-direction: column; }
                .footer-info strong { color: #0f172a; font-size: 0.95rem; }
                .footer-info span { color: #64748b; font-size: 0.75rem; }
                
                .footer-btns { display: flex; gap: 12px; }
                .btn-back { background: white; color: #0f172a; border: 1px solid #e2e8f0; padding: 12px 24px; border-radius: 8px; font-weight: 600; cursor: pointer; }
                .btn-next, .btn-confirm { background: #0f172a; color: white; border: none; padding: 12px 32px; border-radius: 8px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
                .btn-next:disabled, .btn-confirm:disabled { background: #e2e8f0; color: #94a3b8; cursor: not-allowed; }
                .btn-confirm { background: #4f46e5; }
                
                @keyframes fadeInUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                .qonto-loading { min-height: 100vh; display: grid; place-items: center; font-size: 1.1rem; color: #64748b; }
                
                @media (max-width: 768px) {
                    .document-paper { padding: 30px; }
                    .header-inner, .footer-inner { padding: 0 16px; }
                    .steps-indicator { display: none; }
                    .footer-info { display: none; }
                    .footer-btns { width: 100%; }
                    .btn-next, .btn-confirm { flex: 1; }
                }
            `}</style>
        </div>
    );
}
